// g0-blueprint-recheck-before-dispatch.ts
//
// B3 (plan 20260915-1650, phase-00). Before a FEATURE ticket dispatches, re-hash
// the blueprint G0 was approved against and let the approval die if the bytes
// moved — the same primitive G3 already uses for its candidate
// (`deployment-adapter.ts` `recheckG3ImmediatelyBeforeFirstSideEffect` →
// `invalidateGateOnShaChange`). Until this module, G0's hash was written at
// init and never read again: editing BLUEPRINT.md after approval (an appended
// "Sổ quyết định", a scope change) left the approval standing.
//
// After invalidating, G0 is RE-REQUESTED as `pending` for the new bytes with a
// request id derived from the new hash, carrying a reason that names both
// hashes. Why: `approveGate` only accepts a `pending` gate whose request id
// matches (`gate-registry-transitions.ts:83`), and `requestGate("g0", …)` had
// exactly one caller — the initializer. Leaving the record at `invalidated`
// would make every blueprint edit a dead end that only a hand-edit of
// gates.json could undo. Re-requesting is not approving: the human still has
// to `@avatar approve g0 <new id>` after reading the new content, and there is
// deliberately no `autoApprove` path (plan "không làm", B3).
//
// Escape hatches that must NOT fire — each pinned by a guard test:
//  - record without `artifact_hash` (pre-B3 mission, or hand-written gates in
//    tests): invalidating it would turn every running mission into `blocked`
//    at upgrade time;
//  - no `blueprint-binding.json` (pre-B3 mission): no declared path to hash;
//  - declared file absent in the worktree AND the binding does not say init
//    verified it at base_sha (empty-repo null-oid mission, or a binding from
//    before H20): nothing to compare against. When init DID verify it (H20,
//    close-out 2026-09-30), absence means it was removed ⇒ `unreadable`.
// A read error that is NOT "absent" is `unreadable`, and the guard fails
// closed on it: authority over bytes we cannot read is unknown.

import { computeBlueprintHash } from "./blueprint-gate-hash.js";
import { snapshotG0BlueprintFromFile } from "./g0-blueprint-snapshot-store.js";
import { describeDecisionPolicyChange } from "./g0-re-request-decision-policy-change-line.js";
import { approveHint } from "./gate-approve-command-hint.js";
import {
  deriveGateRequestId,
  invalidateGateOnShaChange,
  readGateFile,
  requestGate,
} from "./gate-registry.js";
import {
  readMissionBlueprintBinding,
  resolveBlueprintPathInWorktree,
} from "./mission-blueprint-binding.js";
import { loadCommittedBlueprintBytes } from "./mission-blueprint-committed-bytes-check.js";

export type G0BlueprintRecheckOutcome =
  | { kind: "unchanged" }
  | { kind: "skipped"; reason: string }
  | { kind: "invalidated"; reason: string; requestId: string }
  | { kind: "unreadable"; reason: string };

export interface RecheckG0BlueprintInput {
  controlRoot: string;
  /** The mission worktree — where the declared `source_path` is resolved. */
  repoDir: string;
  now?: () => Date;
}

export async function recheckG0BlueprintBeforeDispatch(
  input: RecheckG0BlueprintInput,
): Promise<G0BlueprintRecheckOutcome> {
  const now = input.now ?? (() => new Date());
  const g0 = (await readGateFile(input.controlRoot)).g0;

  if (g0.artifact_hash === undefined) {
    return {
      kind: "skipped",
      reason: "G0 không ghi artifact_hash (mission trước B3) — không recheck.",
    };
  }
  if (g0.status === "not_requested" || g0.status === "invalidated") {
    return { kind: "skipped", reason: `G0 đang ${g0.status} — không có approval nào để recheck.` };
  }

  const binding = await readMissionBlueprintBinding(input.controlRoot);
  if (binding === undefined) {
    return {
      kind: "skipped",
      reason: "không có blueprint-binding.json (mission trước B3) — không recheck.",
    };
  }

  const blueprintPath = resolveBlueprintPathInWorktree(input.repoDir, binding.source_path);
  let current: string | undefined;
  try {
    current = await computeBlueprintHash(blueprintPath);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return {
      kind: "unreadable",
      reason:
        `không đọc được blueprint "${binding.source_path}" trong worktree (${detail}) — ` +
        "không xác định được G0 còn hiệu lực với bytes nào nên chặn dispatch (fail-closed). Cần người xem xét.",
    };
  }
  if (current === undefined) {
    // H20: init verified the file at base_sha, so the worktree started with it
    // — absent now means something removed it after G0 was bound. Fail closed
    // (G0 left untouched: nothing new to re-request approval for) — unless the
    // worktree's HEAD still has it: an uncommitted deletion left by a rejected
    // attempt, which recoverOwnedCheckout (run right after this) restores
    // (review 260930 M2).
    const committed = binding.verified_at_base_sha
      ? await loadCommittedBlueprintBytes(input.repoDir, binding.source_path)
      : undefined;
    if (committed !== undefined) {
      return {
        kind: "skipped",
        reason: `"${binding.source_path}" chỉ bị xoá ở working tree (HEAD vẫn có) — recovery khôi phục trước dispatch.`,
      };
    }
    if (binding.verified_at_base_sha) {
      return {
        kind: "unreadable",
        reason:
          `BLUEPRINT "${binding.source_path}" đã có ở base_sha lúc init nhưng nay không còn trong worktree — ` +
          "G0 được duyệt trên bytes không còn ai thấy nên chặn dispatch (fail-closed). Cần người xem xét: khôi phục file.",
      };
    }
    return {
      kind: "skipped",
      reason: `"${binding.source_path}" không có trong worktree (mission null-oid hoặc binding trước H20) — giữ nguyên G0.`,
    };
  }

  const approved = g0.artifact_hash.toLowerCase();
  if (current === approved) return { kind: "unchanged" };

  // Approval does not survive a content change (spec §4/§8) — the exact
  // primitive G3 uses, then a fresh pending request for the bytes on disk.
  await invalidateGateOnShaChange(input.controlRoot, "g0", current, now);
  const requestId = deriveGateRequestId("g0", current);
  // V2-1 review M4: policy lật (ask → auto) phải hiện tường minh cho người duyệt lại.
  const policyChange = await describeDecisionPolicyChange({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    sourcePath: binding.source_path,
    approvedHash: approved,
    newBlueprintPath: blueprintPath,
  });
  const reason =
    `BLUEPRINT ("${binding.source_path}") đổi sau khi G0 ${g0.status}: sha256 đã duyệt ${approved} → ` +
    `hiện tại ${current}. Request cũ ${g0.request_id ?? "(none)"} đã huỷ; request mới ${requestId} ` +
    "chờ người duyệt lại — không có đường tự duyệt." +
    (policyChange === undefined ? "" : ` ${policyChange}`);
  // V2-1 (§4.1 nguồn 1): bytes mới mà request này gắn vào — best-effort.
  await snapshotG0BlueprintFromFile(input.controlRoot, blueprintPath, current);
  await requestGate(input.controlRoot, "g0", requestId, current, now, reason);

  return {
    kind: "invalidated",
    requestId,
    reason: `${reason} ${approveHint("g0", requestId)} sau khi đọc nội dung mới.`,
  };
}
